/* Standalone homepage carousel: native swipe also works when this script fails. */
(() => {
  if (customElements.get('idg-hero-carousel')) return;
  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('.idg-hc__track');
      this.slides = [...this.querySelectorAll('.idg-hc__slide')];
      if (!this.track || !this.slides.length) return;
      this.abort = new AbortController();
      const on = (node, type, fn, options = {}) => node.addEventListener(type, fn, { ...options, signal: this.abort.signal });
      this.index = 0;
      this.userPaused = false;
      this.hovered = false;
      this.interacting = false;
      this.automatic = false;
      this.focused = this.contains(document.activeElement);
      this.visible = !('IntersectionObserver' in window);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.controls = this.querySelector('.idg-hc__controls');
      this.playButton = this.querySelector('[data-play]');
      this.dots = [...this.querySelectorAll('[data-slide]')];
      if (this.controls) this.controls.hidden = this.slides.length < 2;
      on(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-prev')) this.go(this.index - 1, true);
        if (button.hasAttribute('data-next')) this.go(this.index + 1, true);
        if (button.hasAttribute('data-slide')) this.go(Number(button.dataset.slide), true);
        if (button.hasAttribute('data-play')) {
          this.userPaused = !this.userPaused;
          this.schedule();
        }
      });
      on(this, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.target !== this.track && !this.controls?.contains(event.target)) return;
        const to = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 }[event.key];
        if (to === undefined) return;
        event.preventDefault();
        this.go(to, true);
      });
      on(this.track, 'scroll', () => {
        clearTimeout(this.timer);
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 130);
      }, { passive: true });
      on(this.track, 'scrollend', () => this.settle());
      on(this.track, 'pointerdown', () => { this.interacting = true; clearTimeout(this.timer); }, { passive: true });
      on(window, 'pointerup', () => { if (this.interacting) { this.interacting = false; this.schedule(); } }, { passive: true });
      on(window, 'pointercancel', () => { this.interacting = false; this.schedule(); }, { passive: true });
      on(this, 'mouseenter', () => { this.hovered = true; this.schedule(); });
      on(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
      on(this, 'focusin', () => { this.focused = true; this.schedule(); });
      on(this, 'focusout', () => queueMicrotask(() => { if (this.abort) { this.focused = this.contains(document.activeElement); this.schedule(); } }));
      on(document, 'visibilitychange', () => this.schedule());
      on(this.motion, 'change', () => this.schedule());
      on(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === String(event.detail?.blockId));
        if (index < 0) return;
        this.userPaused = true;
        this.go(index, true, true);
      });
      on(window, 'resize', () => this.resize(), { passive: true });
      on(this, 'load', () => this.measure(), { capture: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.track);
        if (this.controls) this.resizeObserver.observe(this.controls);
        this.slides.forEach(slide => this.resizeObserver.observe(slide));
      }
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
          this.schedule();
        }, { threshold: [0, 0.15] });
        this.intersectionObserver.observe(this);
      }
      this.width = this.track.clientWidth;
      this.track.scrollLeft = 0;
      this.commit(false);
      this.schedule();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      clearTimeout(this.timer);
      clearTimeout(this.settleTimer);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
    }
    measure() {
      if (this.controls && !this.controls.hidden) {
        const space = `${Math.ceil(this.controls.getBoundingClientRect().height) + 28}px`;
        if (this.style.getPropertyValue('--hc-controls-space') !== space) this.style.setProperty('--hc-controls-space', space);
      }
      const height = Math.ceil(this.slides[this.index].getBoundingClientRect().height);
      if (height && this.track.style.height !== `${height}px`) this.track.style.height = `${height}px`;
    }
    resize() {
      const width = this.track.clientWidth;
      if (width && width !== this.width) {
        this.width = width;
        this.track.scrollTo({ left: this.index * width, behavior: 'instant' });
      }
      this.measure();
    }
    commit(announce) {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, i) => i === this.index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current'));
      if (announce) this.querySelector('.idg-hc__status').textContent = `Slide ${this.index + 1} of ${this.slides.length}`;
      this.measure();
    }
    settle() {
      clearTimeout(this.settleTimer);
      const index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / (this.track.clientWidth || 1))));
      const changed = index !== this.index;
      this.index = index;
      this.commit(changed && !this.automatic);
      this.automatic = false;
      this.schedule();
    }
    go(index, manual = false, instant = false) {
      const next = (index + this.slides.length) % this.slides.length;
      const wraps = Math.abs(next - this.index) > 1;
      this.automatic = !manual;
      this.index = next;
      this.commit(manual);
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior: instant || wraps || this.motion.matches ? 'instant' : 'smooth' });
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.abort) return;
      const reduced = this.motion.matches;
      if (this.playButton) {
        this.playButton.disabled = reduced;
        this.playButton.textContent = reduced || this.userPaused ? 'Play' : 'Pause';
        this.playButton.setAttribute('aria-label', reduced ? 'Slideshow paused for reduced motion' : this.userPaused ? 'Play slideshow' : 'Pause slideshow');
      }
      if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || this.userPaused || this.hovered || this.focused || this.interacting || document.hidden || !this.visible || reduced || window.Shopify?.designMode) return;
      const interval = Math.max(3, Math.min(12, Number(this.dataset.interval) || 5));
      this.timer = setTimeout(() => this.go(this.index + 1), interval * 1000);
    }
  }
  customElements.define('idg-hero-carousel', HeroCarousel);
})();
